import {
  TemplateState,
  FieldsState,
  TemplatePreset,
} from '../../../../store/types/template';
import { TEMPLATE_PRESETS } from '../../../../constants/templatePresets';
import { STAMP_WORDS, getStampImageKey } from './stampCardConstants';

export type ValidationIssue = {
  id: string;
  title: string;
  description: string;
  sectionId?: string;
  deviceTab?: 'apple' | 'google' | 'shared';
};

export type SaveValidationResult = {
  errors: ValidationIssue[];
  warnings: ValidationIssue[];
};

const ANCHOR_HREF_REGEX = /<a\s+href=['"]([^'"]+)['"]/gi;

type FieldsLike = Record<
  string,
  Record<string, { defaultValue?: string | number } | null | undefined>
>;

const collectAnchorHrefs = (fields?: FieldsLike | null): Set<string> => {
  const hrefs = new Set<string>();
  for (const section of Object.values(fields || {})) {
    for (const field of Object.values(section || {})) {
      const value = field?.defaultValue;
      if (typeof value !== 'string') continue;
      for (const match of value.matchAll(ANCHOR_HREF_REGEX)) {
        hrefs.add(match[1]);
      }
    }
  }
  return hrefs;
};

export const validateTemplateSave = (
  state: TemplateState,
  meState: { localisationEnabled?: boolean | null },
  presets: TemplatePreset[] = TEMPLATE_PRESETS
): SaveValidationResult => {
  const errors: ValidationIssue[] = [];
  const warnings: ValidationIssue[] = [];

  // Errors
  if (!meState.localisationEnabled && hasLocalisationContent(state.fields)) {
    errors.push({
      id: 'localisation-not-entitled',
      title: 'Multi-language support',
      description:
        "Your current plan doesn't include multi-language support. Remove the translations from your fields or upgrade your plan.",
      sectionId: 'languages-section',
    });
  }

  // Warnings (grouped by section)
  let anyAppleAppLinking = false;
  let anyGoogleAppLinking = false;
  let anyLocations = false;
  let anyImages = false;
  let anyLinks = false;

  const stateHrefs = collectAnchorHrefs(state.fields as unknown as FieldsLike);

  for (const preset of presets) {
    const d = preset.template_data;

    if (!anyLinks && stateHrefs.size) {
      const presetHrefs = collectAnchorHrefs(d.fields as unknown as FieldsLike);
      for (const href of stateHrefs) {
        if (presetHrefs.has(href)) {
          anyLinks = true;
          break;
        }
      }
    }

    const presetPlayStore =
      (
        d.appLinking?.google?.app as
          | { play_store_url?: string; playStoreUrl?: string }
          | undefined
      )?.play_store_url ??
      (d.appLinking?.google?.app as { playStoreUrl?: string } | undefined)
        ?.playStoreUrl;

    if (
      state.appLinking.appleAppStoreUrl[0] &&
      state.appLinking.appleAppStoreUrl[0] ===
        d.appLinking?.apple?.appStoreUrl?.[0]
    ) {
      anyAppleAppLinking = true;
    }

    if (
      (state.appLinking.googleWebUrl &&
        state.appLinking.googleWebUrl === d.appLinking?.google?.web?.url) ||
      (state.appLinking.googleAppPlayStoreUrl &&
        state.appLinking.googleAppPlayStoreUrl === presetPlayStore)
    ) {
      anyGoogleAppLinking = true;
    }

    // Geofencing
    if (state.locations?.length && d.locations?.length) {
      const matched = state.locations.some((stateLoc) =>
        d.locations!.some(
          (presetLoc) =>
            stateLoc.address === presetLoc.address &&
            stateLoc.latitude === presetLoc.latitude &&
            stateLoc.longitude === presetLoc.longitude
        )
      );
      if (matched) anyLocations = true;
    }

    // Images
    const shared = state.images?.shared;
    const apple = state.images?.apple;
    if (
      (shared?.banner && shared.banner === d.images?.banner) ||
      (shared?.logo && shared.logo === d.images?.logo) ||
      (shared?.icon && shared.icon === d.images?.icon) ||
      (apple?.thumbnail && apple.thumbnail === d.images?.apple?.thumbnail)
    ) {
      anyImages = true;
    }

    if (
      !anyImages &&
      state.templateType === 'stampCard' &&
      d.templateType === 'stampCard'
    ) {
      const presetStamps = (
        d as unknown as {
          stampCard?: { stamps?: Record<string, { image?: string }> };
        }
      ).stampCard?.stamps;
      if (presetStamps) {
        for (const word of STAMP_WORDS) {
          const presetImg = presetStamps[word]?.image;
          if (!presetImg) continue;
          const stateImg = (
            shared as Record<string, string | undefined> | undefined
          )?.[getStampImageKey(word)];
          if (stateImg && stateImg === presetImg) {
            anyImages = true;
            break;
          }
        }
      }
    }
  }

  if (anyAppleAppLinking) {
    warnings.push({
      id: 'appLinking-apple',
      title: 'Apple App Store URL',
      description:
        'Replace the Apple App Store URL with your own iOS app, or remove the link.',
      sectionId: 'app-linking-section',
      deviceTab: 'apple',
    });
  }
  if (anyGoogleAppLinking) {
    warnings.push({
      id: 'appLinking-google',
      title: 'Google Play Store URL',
      description:
        'Replace the Google Play URL, web URL or display text with your own — or remove the link.',
      sectionId: 'app-linking-section',
      deviceTab: 'google',
    });
  }
  if (anyLocations) {
    warnings.push({
      id: 'locations',
      title: 'Geofence locations',
      description:
        'Replace the locations with your own venues or remove them before saving.',
      sectionId: 'locations-section',
      deviceTab: 'shared',
    });
  }
  if (anyImages) {
    warnings.push({
      id: 'images',
      title: 'Images',
      description:
        'Upload your own banner, logo, icon, thumbnail or stamp images.',
      sectionId: 'design-section',
      deviceTab: 'shared',
    });
  }
  if (anyLinks) {
    warnings.push({
      id: 'field-links',
      title: 'Links in fields',
      description:
        'Replace the example links, email addresses and phone numbers in your fields with your own, or remove them.',
      sectionId: 'fields-section',
      deviceTab: 'shared',
    });
  }

  return { errors, warnings };
};

const hasLocalisationContent = (fields: FieldsState): boolean => {
  const sections = ['header', 'central', 'details', 'back'] as const;
  for (const section of sections) {
    const sec = fields?.[section];
    if (!sec) continue;
    for (const slot of Object.keys(sec)) {
      const field = sec[slot];
      if (!field?.localisation) continue;
      for (const lang of Object.keys(field.localisation)) {
        const entry = field.localisation[lang];
        if (entry?.label || entry?.defaultValue) return true;
      }
    }
  }
  return false;
};
